<template>
  <div>        
	<!-- DIALOGS -->
    <SelectionRequiredDialog />
    <RunErrorListDialog :context="context" />
    <DeletionDialog :context="'Selected' + context" />	
    <InlineEditingListpageDialog :listPageContext="context" />
    <IncomeFileImportDialog />
    <MoveIntoCompanyDialog 
        :context="'Income'"
        :message="$t('lang_PUBLISHING_INCOME_DIALOG_CAPTION_MOVE_INCOME')"
    />
    <!-- FixIt dialogs live on list page (not expand panel) so collapse/remount cannot reopen/refetch -->
    <FixItDialog />
    <FixItSF102Dialog :incomeFile="fixItIncomeFile" />
    <FixItC3ErrorDialog :incomeFile="fixItIncomeFile" />
    <FixItRecipientDialog :incomeFile="fixItIncomeFile" />
    <IncomeErrorAiAnalysisDialog :alvqa="alvqa + '-dialog-ai-analysis'" />

    <BaseListPage 
      headline="lang_PUBLISHING_INCOME"
			:context="context"
      :alvqa="alvqa"
      :stickyHeader="true"
    >
			<template v-slot:filter>
				<v-col cols="2">
					<DropdownFilter
						:context="context + 'HalfYearFilter'"
						label="lang_GENERAL_PERIOD_FILTER_OPTION_HALFYEAR"
						:items="halfYearOptions"
						:defaultValueNull="true"
						:alvqa="alvqa + '-list-dropdown-half-year-filter'"
					/>
				</v-col>
			</template>
    <!-- DATA TABLE -->
      <template v-slot:dataTable>
        <BaseDataTableList 
          :headers="headers"
          :context="context"
          :alvqa="alvqa"
          :editInlineFunction="inlineEditing"
          :hasServerSideSorting="true"
          :stickyTopSlot="true"
        >
        <!-- TOPSLOT: actions, imports etc. -->
          <template v-slot:topSlot><IncomeTopSlot /></template>
        <!-- DATA TABLE CONTENT: columns -->
        <template v-slot:body="{ item }">
          <ExpandIcon 
            :isExpanded="item.isExpanded" 
            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-icon-expand'"
            :color="item.item.errorListExists ? 'standardBrownIcon' : 'buttonStandard'"
          /> 

          <td class="ellipsis">
            <BaseTooltip>
              <template v-slot:text>{{ getIncomeSourceText(item, 'tooltip') }}</template>
              <template v-slot:content>
                <span :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-source'">
                  {{ getIncomeSourceText(item, 'text') }}
                </span>
              </template>
            </BaseTooltip>
          </td>


          <td v-if="isColumnVisible('lang_PUBLISHING_INCOME_CAPTION_CATEGORY')" class="ellipsis">
            <BaseTooltip 
              :simpleText="item.item.category" 
              :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-category'"
            />
          </td>
          <td v-if="isColumnVisible('lang_PUBLISHING_INCOME_CAPTION_FILE_NAME')" class="ellipsis">
            <BaseTooltip 
              :simpleText="item.item.fileName" 
              :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-file-name'"
            />
          </td>
          <td 
            class="no_ellipsis text-right"
            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-period'"
          >{{ $alvPlugin.displayPeriod(item.item.period) }}</td>
          <td v-if="isColumnVisible('lang_PUBLISHING_INCOME_CAPTION_IMPORT_DATE')" class="ellipsis">
            <BaseTooltip :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-import-date'">
              <template v-slot:content>{{ $d(new Date(item.item.importDate), 'alvDateTime') }}</template>
              <template v-slot:text>
                {{ item.item.mapping 
                  ? $t('lang_GENERIC_CAPTION_MAPPING') + ': ' + item.item.mapping.title + 
                    ' (' + item.item.mapping.alvId + 
                    (item.item.mapping.abbreviation ? ', ' + item.item.mapping.abbreviation : '') + ')'
                  : (item.item.source === 'multiple' || item.item.source === 'Mehrere' 
                     ? $t('lang_PUBLISHING_INCOME_CAPTION_LEGACY_IMPORTER_TEMPLATE')
                     : $t('lang_PUBLISHING_INCOME_CAPTION_LEGACY_IMPORTER_SOURCE') + ' ' + item.item.source) }}
              </template>
            </BaseTooltip>
          </td>
          <td 
            v-if="isColumnVisible('lang_PUBLISHING_INCOME_CAPTION_RECORDS')"
            class="no_ellipsis text-right"
            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-records'"
          >{{ $alvPlugin.getNumberLocalization(item.item.records, 'numberInteger') }}</td>
          <td 
            v-if="isColumnVisible('lang_PUBLISHING_INCOME_CAPTION_NET')"
            class="no_ellipsis text-right"
            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-netto'"
          >
            <BaseTooltip v-if="item.item.source === 'GEMA'">
              <template v-slot:content>{{ $alvPlugin.formatMoney(item.item.net) }}</template>
              <template v-slot:text>{{ $t("lang_GENERIC_CAPTION_SUM") }} {{ $t('lang_PUBLISHING_INCOME_CAPTION_INCLUSIVE') }} {{ $t('lang_PUBLISHING_INCOME_CAPTION_COMMISSION_PERCENT') }}: {{ $alvPlugin.formatMoney(item.item.sum) }}</template>
            </BaseTooltip>
            <BaseTooltip v-if="item.item.source !== 'GEMA'">
              <template v-slot:content>{{ $alvPlugin.formatMoney(item.item.net) }}</template>
              <template v-slot:text>{{ $t("lang_GENERIC_CAPTION_ORIGINAL_SUM") }} : {{ $alvPlugin.formatMoney(item.item.originalSum) }}</template>
            </BaseTooltip>
          </td>
          <td 
            v-if="isColumnVisible('lang_GENERIC_CAPTION_LIST_NUMBER')"
            class="text-right"
            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-list-number'"
          >{{ item.item.listNumber }}</td>
          <td v-if="isColumnVisible('lang_PUBLISHING_INCOME_CAPTION_CCF')" class="text-right ellipsis">
            <BaseTooltip 
              :simpleText="$alvPlugin.getNumberLocalization(item.item.conversionFactor, 'decimal8')" 
              :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-list-ccf'"
            />
          </td>
        </template>
      <!-- EXPAND: Read Only View -->
					<template v-slot:expandSlot="{ id }"><IncomeReadOnly :itemId='id' /></template>
        </BaseDataTableList>
      </template>
    </BaseListPage>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { isListColumnVisible } from '@/utils/listColumnSettings'

import BaseDataTableList from '@/components/_base/BaseDataTableList'
import BaseListPage from '@/components/_base/BaseListPage'
import BaseTooltip from '@/components/_base/BaseTooltip'
import ExpandIcon from '@/components/_buttons/ExpandIcon'
import DropdownFilter from '@/components/_filters/DropdownFilter'

import IncomeReadOnly from './_readonly/IncomeReadOnly'
import IncomeTopSlot from './IncomeTopSlot'

import IncomeFileImportDialog from './_dialogs/IncomeFileImportDialog'
import FixItDialog from './_dialogs/FixItDialog'
import FixItSF102Dialog from './_dialogs/FixItSF102Dialog'
import FixItC3ErrorDialog from './_dialogs/FixItC3ErrorDialog'
import FixItRecipientDialog from './_dialogs/FixItRecipientDialog'
import IncomeErrorAiAnalysisDialog from './_dialogs/IncomeErrorAiAnalysisDialog'
import SelectionRequiredDialog from '@/components/_dialogs/SelectionRequiredDialog'
import RunErrorListDialog from '@/components/_dialogs/RunErrorListDialog'
import DeletionDialog from '@/components/_dialogs/DeletionDialog'
import InlineEditingListpageDialog from '@/components/_dialogs/InlineEditingListpageDialog'
import MoveIntoCompanyDialog from '@/components/_dialogs//MoveIntoCompanyDialog'


export default {
  name: 'IncomeList',
  components: {
    BaseDataTableList,
    BaseListPage,
    BaseTooltip,
    ExpandIcon,
    DropdownFilter,

    IncomeReadOnly,
    IncomeTopSlot,

    IncomeFileImportDialog,
    FixItDialog,
    FixItSF102Dialog,
    FixItC3ErrorDialog,
    FixItRecipientDialog,
    IncomeErrorAiAnalysisDialog,
    SelectionRequiredDialog,
    RunErrorListDialog,
    DeletionDialog,
    InlineEditingListpageDialog,
    MoveIntoCompanyDialog
  },
  data() {
    return {
      alvqa: "publishing-income",
      context: "Income"
    }
  },
  computed: {
    ...mapGetters(['getIncomeSearch', 'getPublishingSettings']),

    halfYearOptions() {
      const halfYears = []
      const today = new Date()
      const maxYear = today.getFullYear()
      const currentHalfYear = today.getMonth() < 6 ? 1 : 2
      for(let year = maxYear; year >= 2008; year--) {
        for(let halfYear = year === maxYear ? currentHalfYear : 2; halfYear >= 1; halfYear--) {
          halfYears.push({
            text: this.$alvPlugin.displayPeriod({ num: halfYear, period: 'halfyear', year }),
            value: year + '-' + halfYear,
            alvqa: this.alvqa + '-dropdown-half-year-' + year + '-' + halfYear
          })
        }
      }
      return halfYears
    },

    // List-row income file for FixIt dialogs (expand panel no longer hosts them)
    fixItIncomeFile() {
      const expanded = this.$store.getters.getExpandedIncome
      if (!expanded || !expanded.length) return {}
      const details = expanded[0]
      const fromList = this.$store.getters.getIncome.find(
        (file) => file.id === details.id || file.alvId === details.alvId || file.fileName === details.fileName
      )
      return fromList || details || {}
    },

    /** Returns an Array with header-objects with props.
    *  @return {[{text: String, align: string, sortable: string, value: string}]} props of the header */    
    headers() {
      return [
				{
					text: '', 
					value: 'data-table-select', 
					align: 'center',
          width: "60px",
          sortable: false,
          class: 'text-no-wrap'
        },        
				{
					text: '', 
					value: 'data-table-expand',
					align: 'center', 
					width: "48px",
          sortable: false,
					class: 'text-no-wrap'
				},
        {
          text: this.$t('lang_GENERIC_CAPTION_SOURCE'), 
          value: 'source',
          align: 'left', 
          sortable: false,
          class: 'text-no-wrap'
        },
        {
          text: this.$t('lang_PUBLISHING_INCOME_CAPTION_CATEGORY'), 
          value: 'category',
          align: 'left', 
          sortable: false,
          width: "90px",
          class: 'text-no-wrap',
          settingKey: 'lang_PUBLISHING_INCOME_CAPTION_CATEGORY'
        },
        {
          text: this.$t('lang_PUBLISHING_INCOME_CAPTION_FILE_NAME'), 
          value: 'fileName',
          align: 'left', 
          sortable: false,
          class: 'text-no-wrap',
          settingKey: 'lang_PUBLISHING_INCOME_CAPTION_FILE_NAME'
        },
        {
          text: this.$t('lang_GENERIC_CAPTION_PERIOD'), 
          value: 'period', 
          align: 'end', 
          sortable: true,
          width: "150px",
          class: 'text-no-wrap'
        },
        {
          text: this.$t('lang_PUBLISHING_INCOME_CAPTION_IMPORT_DATE'), 
          value: 'importDate',
          align: 'left', 
          sortable: true,
          width: '180px',
          class: 'text-no-wrap',
          settingKey: 'lang_PUBLISHING_INCOME_CAPTION_IMPORT_DATE'
        },
        {
          text: this.$t('lang_PUBLISHING_INCOME_CAPTION_RECORDS'), 
          value: 'records',
          align: 'right', 
          sortable: false,
          width: "100px",
          class: 'text-no-wrap',
          settingKey: 'lang_PUBLISHING_INCOME_CAPTION_RECORDS'
        },
        {
          text: this.$t('lang_PUBLISHING_INCOME_CAPTION_NET') + ' ' + this.$store.getters.getSelectedCurrency, 
          value: 'net',
          align: 'right', 
          sortable: false,
          width: '120px',
          class: 'text-no-wrap',
          settingKey: 'lang_PUBLISHING_INCOME_CAPTION_NET'
        },
        {
          text: this.$t("lang_GENERIC_CAPTION_LIST_NUMBER"), 
          value: 'listNumber',
          align: 'right', 
          sortable: false,
          width: '90px',
          class: 'text-no-wrap',
          settingKey: 'lang_GENERIC_CAPTION_LIST_NUMBER'
        },
        {
          text: this.$t("lang_PUBLISHING_INCOME_CAPTION_CCF"), 
          value: 'conversionFactor',
          align: 'right', 
          sortable: false,
          width: '90px',
          class: 'text-no-wrap',
          settingKey: 'lang_PUBLISHING_INCOME_CAPTION_CCF'
        },
        {
          text: '', 
          value: 'downloadLink',
          align: 'right',
					sortable: false,
          width: "70px",
          class: 'text-no-wrap'
        },
        {
          text: '', 
          value: 'inlineEdit',
          align: 'right',
					sortable: false,
          width: "70px",
          class: 'text-no-wrap'
        }
      ].filter(header => !header.settingKey || this.isColumnVisible(header.settingKey))
    },

  },
  methods: {  
    isColumnVisible(settingKey) {
      return isListColumnVisible(
        this.getPublishingSettings,
        'lang_GENERIC_CAPTION_DOMAIN_PUBLISHING_INCOME',
        'lang_SETTINGS_PUBLISHING_INCOME_LISTPAGE',
        settingKey
      )
    },
    getIncomeSourceText(item, type) {
      if (type == 'tooltip') {
        let tooltip = item.item.source == 'multiple' ? this.$t('lang_GENERAL_CAPTION_MULTIPLE') : item.item.source + ' (' + item.item.sourceAlvId + ')' 
        return tooltip
      } else { // return name of source
        let sourceName = item.item.source == 'multiple' ? this.$t('lang_GENERAL_CAPTION_MULTIPLE') : item.item.source
        return sourceName
      }
    },
    inlineEditing(item) {
      this.$store.commit('setSelectedIncome', [])
      this.$store.commit('setIsAllSelectedIncome', false)
      
      this.$store.commit('setSelectedIncome', item.alvId)

      // Fill Dialog data fields
      let details = this.$store.getters.getIncome.find(element => {
          return element.alvId === item.alvId
      })

      let detailsDeepCopy = JSON.parse(JSON.stringify(details))
      // format numbers to ui or acc setting before showing in data fields.
      if(detailsDeepCopy.commissionPercent !== null) {
        detailsDeepCopy.commissionPercent = this.$i18n.n(detailsDeepCopy.commissionPercent, 'decimal3')
      }
      
      if(detailsDeepCopy.conversionFactor !== null){
        detailsDeepCopy.conversionFactor = this.$i18n.n(detailsDeepCopy.conversionFactor, 'decimal8') 
      }


      this.$store.commit('setIncomeInlineEditingDialogDetails', detailsDeepCopy)
      this.$store.commit('setShowInlineEditingDialog', true)
    },
  },
  created() {
    this.$store.commit('setSelectedIncomeHalfYearFilter', null)
    // fetch array income importers mappings
    this.$store.dispatch('fetchIncomeImporters');
    this.$store.dispatch('fetchErrorCodes')
    this.$store.dispatch('fetchPublishingSettings')
  },
  beforeDestroy() {
    this.$store.commit('resetIncomeFixItDialogs')
  }
}
</script>
